import React from "react";
import { Link } from "gatsby";
import { IoMdArrowForward } from "@react-icons/all-files/io/IoMdArrowForward";
import { FiActivity } from "@react-icons/all-files/fi/FiActivity";
import { FiBarChart2 } from "@react-icons/all-files/fi/FiBarChart2";
import { FiBell } from "@react-icons/all-files/fi/FiBell";
import { FiDatabase } from "@react-icons/all-files/fi/FiDatabase";
import { FiMonitor } from "@react-icons/all-files/fi/FiMonitor";
import { FiShare2 } from "@react-icons/all-files/fi/FiShare2";
import { FiTarget } from "@react-icons/all-files/fi/FiTarget";
import { FiTrendingUp } from "@react-icons/all-files/fi/FiTrendingUp";
import { FiUsers } from "@react-icons/all-files/fi/FiUsers";
import { FiVolume2 } from "@react-icons/all-files/fi/FiVolume2";
import { Container } from "~/atoms/Container";
import { PATH } from "~/constant/path";
import { colors } from "~/utils/colors";
import { breakPoints } from "~/utils/variables";

/** Refcomeのデザインシステムのブルー */
const BLUE = "#779cf7";

const SERVICES = [
  {
    id: "consulting",
    tone: "brand" as const,
    to: PATH.REQUEST.to,
    icon: FiUsers,
    label: "人による伴走支援",
    title: "リファラル特化型RPO",
    summary: "プロが企画から実行・改善まで支援します。",
    features: [
      { icon: FiTarget, title: "戦略設計", description: "制度・インセンティブ・紹介フローを設計" },
      { icon: FiVolume2, title: "社内への浸透", description: "説明会や社内向けコンテンツで制度を浸透" },
      { icon: FiTrendingUp, title: "紹介活性化", description: "紹介しやすい導線づくりや施策を実行" },
      { icon: FiBarChart2, title: "効果検証・改善", description: "KPIをもとに効果を検証し、施策を見直し" },
    ],
  },
  {
    id: "teams",
    tone: "blue" as const,
    to: PATH.REQUEST.to,
    icon: FiMonitor,
    label: "ツールによる運用支援",
    title: "リファラル活性化システム",
    summary: "紹介をスムーズにし、人事の運用・効果測定を支えます。",
    features: [
      { icon: FiShare2, title: "かんたん紹介フロー", description: "URLやQRコードから、かんたんに紹介" },
      { icon: FiDatabase, title: "タレントプール", description: "紹介候補者や辞退者・アルムナイを一元管理" },
      { icon: FiBell, title: "社内広報・通知", description: "記事配信やプッシュ通知で社内に情報発信" },
      { icon: FiActivity, title: "リファラルスコア®", description: "社員の紹介しやすさを8つの要素で可視化" },
    ],
  },
];

export const Services: React.FC<{}> = () => {
  return (
    <>
      <div id="aboutReferral" className="anchor" />
      <section className="section">
        <Container>
          <div className="head">
            <p className="eyebrow">リファラル採用の実行と定着を支える</p>
            <h2 className="title">Refcomeの2つの支援</h2>
            <p className="lead">
              リファラル採用のプロによる伴走支援と、社内浸透や効果測定を支えるクラウドシステム。
              <br className="leadBreak" />
              制度設計から運用・改善まで、一貫して支援します。
            </p>
          </div>

          <div className="cards">
            {SERVICES.map((service, index) => {
              const HeroIcon = service.icon;
              return (
                <React.Fragment key={service.id}>
                  {index > 0 ? (
                    <div className="cross" aria-hidden="true">
                      ×
                    </div>
                  ) : null}
                  <article className={service.tone === "blue" ? "card cardBlue" : "card cardBrand"}>
                    <Link to={service.to}>
                      <div className="cardHead">
                        {service.tone === "blue" ? (
                          <span className="cardDevices" aria-hidden="true">
                            <img className="cardLaptop" src="/hero/hero-laptop.png" alt="" loading="lazy" />
                            <img className="cardPhone" src="/hero/hero-phone.png" alt="" loading="lazy" />
                          </span>
                        ) : (
                          <span className="cardPhoto" aria-hidden="true" />
                        )}
                        <span className="cardGlow" aria-hidden="true" />
                        <div className="cardHeadMain">
                          <span className="cardIcon">
                            <HeroIcon />
                          </span>
                          <p className="cardLabel">{service.label}</p>
                          <h3 className="cardTitle">{service.title}</h3>
                          <p className="cardSummary">{service.summary}</p>
                        </div>
                      </div>
                      <ul className="features">
                        {service.features.map((feature) => {
                          const Icon = feature.icon;
                          return (
                            <li key={feature.title} className="feature">
                              <span className="featureIcon">
                                <Icon />
                              </span>
                              <span className="featureBody">
                                <span className="featureTitle">{feature.title}</span>
                                <span className="featureText">{feature.description}</span>
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                      <span className="cardMore">
                        詳しい資料をダウンロード
                        <IoMdArrowForward />
                      </span>
                    </Link>
                  </article>
                </React.Fragment>
              );
            })}
          </div>
        </Container>
      </section>

      <style jsx>{`
        .anchor {
          display: block;
          height: 90px;
          margin-top: -90px;
          visibility: hidden;
        }

        .section {
          padding: 88px 24px 96px;
          background: ${colors.lightOrange};
        }
        .section :global(.container) {
          display: block;
          width: 100%;
          max-width: 1000px;
        }

        /* ---------- 見出し ---------- */
        .head {
          text-align: center;
        }
        .eyebrow {
          margin: 0;
          color: ${colors.defaultText};
          font-size: 24px;
          font-weight: bold;
          line-height: 1.5;
        }
        .title {
          margin: 4px 0 0;
          color: ${colors.defaultText};
          font-size: 48px;
          font-weight: bold;
          line-height: 1.3;
          letter-spacing: 0.02em;
        }
        .lead {
          margin: 24px 0 0;
          color: ${colors.defaultText};
          font-size: 18px;
          line-height: 1.9;
        }

        /* ---------- カード ---------- */
        .cards {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
          align-items: stretch;
          column-gap: 24px;
          margin-top: 44px;
        }
        .cross {
          align-self: center;
          color: ${colors.defaultText};
          font-size: 30px;
          font-weight: bold;
          line-height: 1;
          opacity: 0.6;
        }
        .card {
          display: flex;
          overflow: hidden;
          border-radius: 20px;
          background: ${colors.white};
          box-shadow: 0 14px 36px rgba(255, 100, 0, 0.1);
          transition: transform 0.25s ease, box-shadow 0.25s ease;
        }
        .card:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 44px rgba(255, 100, 0, 0.16);
        }
        /* カード全体をリンクにする */
        .card :global(a) {
          display: flex;
          flex-direction: column;
          width: 100%;
          color: inherit;
          text-decoration: none;
        }

        /* ---------- カード上部の色面 ---------- */
        .cardHead {
          position: relative;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          padding: 26px 24px 24px;
        }
        /*
          1列目: アイコン（ラベル＋タイトルの行だけ）
          2列目: ラベル / タイトル / 説明文（左端を揃える）
        */
        .cardHeadMain {
          position: relative;
          display: grid;
          grid-template-columns: auto minmax(0, 1fr);
          grid-template-rows: auto auto auto;
          column-gap: 14px;
          align-items: start;
        }
        .cardBrand .cardHead {
          background: linear-gradient(135deg, #ec7f18 0%, #ec553f 100%);
        }
        .cardBlue .cardHead {
          background: linear-gradient(135deg, ${BLUE} 0%, #5f80ee 100%);
        }
        /* トップのキービジュアルと同じ写真を、色面にうっすら重ねる */
        .cardPhoto {
          position: absolute;
          inset: 0;
          background-image: url("/hero/hero-photo.jpg");
          background-size: cover;
          background-position: 78% 28%;
          opacity: 0.22;
          mix-blend-mode: soft-light;
          pointer-events: none;
        }
        /* PCとスマホの重なり方はヒーローのまま、かたまりごと右下に寄せる */
        .cardDevices {
          position: absolute;
          right: -80px;
          bottom: -120px;
          left: auto;
          top: auto;
          width: 92%;
          /* 左カードの写真と見え方の強さを揃える */
          opacity: 0.2;
          pointer-events: none;
          -webkit-mask-image: linear-gradient(115deg, transparent 8%, rgba(0, 0, 0, 0.55) 42%, #000 78%);
          mask-image: linear-gradient(115deg, transparent 8%, rgba(0, 0, 0, 0.55) 42%, #000 78%);
        }
        .cardLaptop {
          position: relative;
          z-index: 1;
          display: block;
          width: 100%;
          height: auto;
          filter: sepia(0.08) saturate(0.92) brightness(0.99) drop-shadow(0 12px 20px rgba(40, 28, 18, 0.18));
        }
        .cardPhone {
          position: absolute;
          z-index: 2;
          left: 6%;
          bottom: 5%;
          height: 54%;
          width: auto;
          filter: sepia(0.08) saturate(0.92) brightness(0.99) drop-shadow(0 10px 16px rgba(40, 28, 18, 0.16));
        }
        /* 色面に奥行きを出すやわらかい光 */
        .cardGlow {
          position: absolute;
          top: -70px;
          right: -60px;
          width: 220px;
          height: 220px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(255, 255, 255, 0.24) 0%, rgba(255, 255, 255, 0) 70%);
          pointer-events: none;
        }
        .cardIcon {
          position: relative;
          grid-column: 1;
          grid-row: 1 / 3;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          /* タイトルが折り返してもサイズが変わらないよう固定する */
          align-self: start;
          width: 52px;
          height: 52px;
          border-radius: 14px;
          background: rgba(255, 255, 255, 0.18);
          border: 1px solid rgba(255, 255, 255, 0.28);
          box-sizing: border-box;
        }
        .cardIcon :global(svg) {
          width: 24px;
          height: 24px;
          color: ${colors.white};
        }
        .cardLabel {
          grid-column: 2;
          grid-row: 1;
          margin: 0;
          color: rgba(255, 255, 255, 0.8);
          font-size: 15px;
          font-weight: bold;
          letter-spacing: 0.08em;
          line-height: 1.4;
        }
        .cardTitle {
          grid-column: 2;
          grid-row: 2;
          margin: 4px 0 0;
          color: ${colors.white};
          font-size: 23px;
          font-weight: bold;
          line-height: 1.35;
        }
        .cardSummary {
          grid-column: 2;
          grid-row: 3;
          margin: 10px 0 0;
          color: rgba(255, 255, 255, 0.86);
          font-size: 13px;
          line-height: 1.7;
        }

        /* ---------- 機能リスト ---------- */
        .features {
          display: flex;
          flex-direction: column;
          margin: 0;
          padding: 8px 24px 12px;
          list-style: none;
        }
        .feature {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 15px 0;
        }
        .feature + .feature {
          border-top: 1px solid ${colors.creamGray2};
        }
        .featureIcon {
          flex: none;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: ${colors.lightOrange};
        }
        .featureIcon :global(svg) {
          width: 17px;
          height: 17px;
          color: ${colors.brand};
        }
        /* ツール側は上の色面に合わせてブルーで統一する */
        .cardBlue .featureIcon {
          background: #edf2fe;
        }
        .cardBlue .featureIcon :global(svg) {
          color: ${BLUE};
        }
        .featureBody {
          display: flex;
          flex-direction: column;
          gap: 3px;
        }
        .featureTitle {
          color: ${colors.defaultText};
          font-size: 17px;
          font-weight: bold;
          line-height: 1.4;
        }
        .featureText {
          color: ${colors.lightText};
          font-size: 14px;
          line-height: 1.6;
        }

        /* ---------- CTAボタン ---------- */
        .cardMore {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin: auto 22px 22px;
          padding: 15px 20px;
          border-radius: 999px;
          background: linear-gradient(90deg, #ec7f18 0%, #ec553f 100%);
          color: ${colors.whiteText};
          font-size: 15px;
          font-weight: bold;
          letter-spacing: 0.02em;
          box-shadow: 0 8px 18px rgba(236, 100, 40, 0.3);
          transition: box-shadow 0.25s ease, filter 0.25s ease;
        }
        .cardBlue .cardMore {
          background: linear-gradient(90deg, ${BLUE} 0%, #5f80ee 100%);
          box-shadow: 0 8px 18px rgba(119, 156, 247, 0.38);
        }
        .card:hover .cardMore {
          filter: brightness(1.05);
          box-shadow: 0 12px 24px rgba(236, 100, 40, 0.38);
        }
        .cardBlue:hover .cardMore {
          box-shadow: 0 12px 24px rgba(119, 156, 247, 0.46);
        }
        .cardMore :global(svg) {
          width: 17px;
          height: 17px;
          transition: transform 0.25s ease;
        }
        .card:hover .cardMore :global(svg) {
          transform: translateX(4px);
        }

        /* ---------- タブレット ---------- */
        @media (${breakPoints.tablet}) {
          .section {
            padding: 64px 24px 72px;
          }
          .title {
            font-size: 40px;
          }
          .lead {
            font-size: 16px;
          }
          .leadBreak {
            display: none;
          }
          .cards {
            grid-template-columns: minmax(0, 1fr);
            justify-items: stretch;
            row-gap: 16px;
            margin-top: 32px;
          }
          .cross {
            justify-self: center;
          }
          .card {
            max-width: 560px;
            width: 100%;
            margin: 0 auto;
          }
        }

        /* ---------- スマホ ---------- */
        @media (${breakPoints.sp}) {
          .section {
            padding: 48px 16px 56px;
          }
          .eyebrow {
            font-size: 17px;
          }
          .title {
            font-size: 28px;
          }
          .lead {
            margin-top: 12px;
            font-size: 14px;
            line-height: 1.8;
          }
          .cards {
            margin-top: 24px;
          }
          .cross {
            font-size: 24px;
          }
          .card {
            border-radius: 16px;
          }
          .card:hover {
            transform: none;
          }
          .cardHead {
            padding: 20px 18px;
          }
          .cardHeadMain {
            column-gap: 12px;
          }
          .cardIcon {
            width: 44px;
            height: 44px;
            border-radius: 12px;
          }
          .cardIcon :global(svg) {
            width: 20px;
            height: 20px;
          }
          .cardLabel {
            font-size: 13px;
          }
          .cardTitle {
            font-size: 19px;
          }
          .cardSummary {
            font-size: 12px;
          }
          .features {
            padding: 4px 18px 8px;
          }
          .cardMore {
            margin: auto 16px 16px;
            padding: 13px 16px;
            font-size: 13.5px;
          }
          .feature {
            gap: 12px;
            padding: 13px 0;
          }
          .featureIcon {
            width: 32px;
            height: 32px;
          }
          .featureIcon :global(svg) {
            width: 15px;
            height: 15px;
          }
          .featureTitle {
            font-size: 15px;
          }
          .featureText {
            font-size: 12.5px;
          }
        }
      `}</style>
    </>
  );
};
